import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, Search, Info } from "react-feather";
import "../styles/components/ShortCuts.css";

const flattenMenuItems = (items) => {
  const flatMap = new Map();

  const processItem = (item, parent) => {
    const code = item.menu_code || "";
    const title = item.display_title;

    if (item.status === "active" && code.trim() !== "") {
      const key = `${code}_${title}`;
      if (!flatMap.has(key)) {
        const parentPath = parent
          ? parent.display_title.toLowerCase().replace(/ /g, "-")
          : "";
        const childPath = title.toLowerCase().replace(/ /g, "-");
        const isReport =
          parent &&
          (parent.display_title === "Graphical Reports" ||
            parent.display_title === "Reports");
        flatMap.set(key, {
          title,
          code,
          parentPath,
          childPath,
          isReport,
          parentTitle: parent?.display_title,
        });
      }
    }

    if (Array.isArray(item.child_menu)) {
      item.child_menu.forEach((child) => processItem(child, item));
    }
  };

  items.forEach((item) => processItem(item, null));
  return Array.from(flatMap.values());
};

const ShortCuts = () => {
  const [menuList, setMenuList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);
  const [pendingNav, setPendingNav] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedItems = localStorage.getItem("sidebarItems");
    if (storedItems) {
      try {
        const parsed = JSON.parse(storedItems);
        const flattened = flattenMenuItems(parsed);
        setMenuList(flattened);
      } catch (err) {
        console.error("Failed to parse sidebarItems", err);
      }
    }
  }, []);

  const handleCopy = useCallback((item) => {
    const text = `\`${item.code}`;
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    });
    setCopiedCode(item.code);
    setPendingNav(item);
    setTimeout(() => setCopiedCode(null), 3000);
  }, []);

  const filteredList = menuList.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.code.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const firstItem = menuList[0];

  return (
    <div className="sk-page">
      <div className="sk-card">
        <div className="sk-header">
          <button className="sk-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <div className="sk-title-block">
            <span className="sk-keyboard-icon">⌨</span>
            <div>
              <h2 className="sk-title">Shortcut Keys</h2>
              <p className="sk-subtitle">
                Use shortcuts to save time and improve your productivity.
              </p>
            </div>
          </div>
          <div className="sk-search-wrap">
            <Search size={15} className="sk-search-icon" />
            <input
              className="sk-search-input"
              placeholder="Search shortcut..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="sk-howto-wrap">
          <div className="sk-howto-box">
            <div className="sk-howto-title">
              <Info size={16} className="sk-info-icon" />
              How to use shortcuts?
            </div>
            <ol className="sk-howto-steps">
              <li>Press the backtick ( ` ) key</li>
              <li>Type the menu code</li>
              <li>The page will open automatically</li>
            </ol>
            <div className="sk-howto-demo">
              <div className="sk-howto-demo-col">
                <span className="sk-key">`</span>
              </div>
              <span className="sk-plus">+</span>
              {(firstItem?.code ?? "CCR").split("").map((letter, i, arr) => (
                <React.Fragment key={i}>
                  <span className="sk-key sk-key-letter">{letter}</span>
                  {i < arr.length - 1 && <span className="sk-plus">+</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="sk-example-box">
            <div className="sk-example-title">Example</div>
            <div className="sk-example-row">
              <span className="sk-badge">`{firstItem?.code}</span>
              <span className="sk-eq">=</span>
              <span className="sk-example-desc">
                {firstItem?.title ?? "Customer complaint register"}
              </span>
            </div>
            <div className="sk-tip-box">
              <span className="sk-tip-icon">💡</span>
              <span>
                <strong>Tip:</strong> You can click the copy icon to copy the
                shortcut with backtick and menu code. Then press{" "}
                <kbd>Ctrl+V</kbd> to navigate to that page.
              </span>
            </div>
          </div>
        </div>

        <div className="sk-list-section">
          <div className="sk-list-header">
            <span className="sk-list-title">All Shortcuts</span>
          </div>
          <p className="sk-list-hint">
            <Info size={13} />
            &nbsp;Click the copy icon to copy the shortcut (includes backtick).
            {pendingNav && (
              <span className="sk-nav-hint">
                &nbsp;Press <kbd>Ctrl+V</kbd> to navigate to{" "}
                <strong>{pendingNav.title}</strong>.
              </span>
            )}
          </p>

          <div className="sk-list">
            {filteredList.map((item) => (
              <div
                key={`${item.code}_${item.title}`}
                className={`sk-row${pendingNav?.code === item.code ? " sk-row--pending" : ""}`}
              >
                <span className="sk-row-badge">{item.code}</span>
                <span className="sk-row-eq">=</span>
                <span className="sk-row-title">{item.title}</span>
                <span className="sk-row-shortcut">` {item.code}</span>
                <button
                  className="sk-copy-btn"
                  title="Copy shortcut (then press Ctrl+V to navigate)"
                  onClick={() => handleCopy(item)}
                >
                  {copiedCode === item.code ? (
                    <Check size={14} className="sk-copied-icon" />
                  ) : (
                    <Copy size={14} />
                  )}
                </button>
              </div>
            ))}
            {filteredList.length === 0 && (
              <p className="sk-empty">No shortcuts found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortCuts;
